import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PrivacyPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PrivacyPolicyDialog({ open, onOpenChange }: PrivacyPolicyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Privacy Policy</DialogTitle>
          <DialogDescription>
            Last updated: {new Date().toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm leading-relaxed">
            <section>
              <h3 className="font-semibold text-base mb-3">1. Introduction</h3>
              <p>
                SAASGAMA ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
                how we collect, use, disclose, and safeguard your information when you use our SaaS management platform 
                and related services.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">2. Information We Collect</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Account Information:</h4>
                  <p>Name, email address, company name, phone number, and billing information.</p>
                </div>
                <div>
                  <h4 className="font-medium">SaaS Management Data:</h4>
                  <p>Information about your software subscriptions, licenses, usage analytics, renewal dates, and cost management data.</p>
                </div>
                <div>
                  <h4 className="font-medium">Technical Information:</h4>
                  <p>IP address, browser type, device information, cookies, and usage analytics.</p>
                </div>
                <div>
                  <h4 className="font-medium">Communication Data:</h4>
                  <p>Messages you send us and records of our interactions with you.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">3. How We Use Your Information</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Provide and maintain our SaaS management platform</li>
                <li>Process transactions and manage your subscriptions</li>
                <li>Send you renewal notifications and cost optimization insights</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve our services and develop new features</li>
                <li>Comply with legal obligations and protect against fraud</li>
                <li>Send marketing communications (with your consent)</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">4. Information Sharing and Disclosure</h3>
              <p>We do not sell your personal information. We may share your information in these limited circumstances:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (payment processors, email services, analytics providers)</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">5. Data Security</h3>
              <p>
                We implement industry-standard security measures including encryption, secure data transmission, 
                access controls, and regular security audits to protect your information. However, no method of 
                electronic storage or transmission is 100% secure.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">6. Your Rights</h3>
              <p>Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Object:</strong> Object to certain processing activities</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, contact us at privacy@saasgama.com.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">7. Cookies and Tracking</h3>
              <p>
                We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, 
                and provide personalized content. You can manage your cookie preferences through your browser settings 
                or our cookie banner.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">8. Data Retention</h3>
              <p>
                We retain your personal information for as long as necessary to provide our services and comply with 
                legal obligations. Account information is typically retained for 7 years after account closure for 
                compliance purposes.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">9. International Transfers</h3>
              <p>
                Your information may be transferred to and processed in countries outside your residence. We ensure 
                appropriate safeguards are in place for international transfers, including standard contractual clauses 
                and adequacy decisions.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">10. Children's Privacy</h3>
              <p>
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect 
                personal information from children under 18.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">11. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes via email 
                or through our platform. Continued use of our services after changes constitutes acceptance of the 
                updated policy.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-3">12. Contact Us</h3>
              <p>
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="mt-2 space-y-1">
                <p><strong>Email:</strong> privacy@saasgama.com</p>
                <p><strong>Address:</strong> SAASGAMA Privacy Team</p>
                <p><strong>Phone:</strong> +1 (555) 123-4567</p>
              </div>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}